import React, {useCallback, useEffect, useRef, useState} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import useTheme from "../../../components/web/layoutscommon/DarkModeStorage";
import btorageBooster from "../../../assets/img/btorageBooster.png";
import StoreShell from "./StoreShell";
import {useStoreWebSocket} from "../../../services/StoreWebsocketContext";
import {STORE_CHAT_READ_EVENT} from "./storeChatEvents";
import {storeLogout, storeGet, STORE_LOGIN_PATH, STORE_DASHBOARD_PATH} from "../../../services/storeApi";
import {TbLayoutDashboard, TbApi, TbMessageDots, TbMail, TbBrandHipchat, TbUserCog, TbLogout, TbMenu2, TbX} from "react-icons/tb";

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  상점사(쇼핑몰) 대시보드 공통 레이아웃
 * ════════════════════════════════════════════════════════════════════════════════
 *  비토리지 고객 헤더/푸터와 중첩되지 않는 '단독 배치' 레이아웃이다.
 *
 *  좌측 메뉴는 네모난 사이드바가 아니라 여명 위에 떠 있는 '유리 레일' 이다.
 *  선택된 항목만 대표색으로 켜지고 나머지는 조용히 둔다.
 *  1024px 미만에서는 레일을 숨기고 헤더의 햄버거로 전체 메뉴를 펼친다.
 *  (가로 스크롤 탭은 메뉴가 하나만 보여 불편해서 걷어냈다)
 * ════════════════════════════════════════════════════════════════════════════════
 */
function StoreLayout({children, active, title, desc, eyebrow, titleClassName}) {

    const [theme] = useTheme();
    const navigate = useNavigate();
    const location = useLocation();

    const [companyName, setCompanyName] = useState("");
    const [unreadChat, setUnreadChat] = useState(0);
    const [menuOpen, setMenuOpen] = useState(false);

    // 미확인 배지를 실시간으로 갱신하기 위해 상점사 전용 웹소켓을 구독한다
    const {storeSubscribe} = useStoreWebSocket();

    const [storeSeqNum, setStoreSeqNum] = useState(null);

    /** 미확인 메세지 수 재조회 */
    const refreshUnread = useCallback(() => {
        storeGet('/storeChat/store/unread')
            .then(res => setUnreadChat(Number(res?.data) || 0))
            .catch(() => { /* 실패 시 직전 값 유지 */ });
    }, []);

    useEffect(() => {
        storeGet('/store/myInfo')
            .then(res => {
                setCompanyName(res?.data?.companyName || "");
                setStoreSeqNum(res?.data?.seqNum ?? null);
            })
            .catch(() => { /* 가드에서 이미 처리 */ });

        refreshUnread();
    }, [location.pathname, refreshUnread]);

    /*
     * 미확인 배지를 실시간으로 맞춘다.
     *  - 웹소켓 : 새 메세지가 오거나 상대가 읽으면 다시 센다.
     *  - 읽음신호 : 채팅화면이 목록을 불러오면(=서버에서 읽음처리가 끝나면) 다시 센다.
     *    이 두 가지 덕분에 채팅을 보고 있는 동안에는 배지가 올라가지 않는다.
     *    (메세지 도착 → 화면이 목록을 다시 받으며 읽음처리 → 배지 0 유지)
     */
    useEffect(() => {
        if (!storeSeqNum) return;
        return storeSubscribe(
            `store-${storeSeqNum}`,
            `/topic/storeChat/${storeSeqNum}`,
            () => refreshUnread(),
        );
    }, [storeSeqNum, storeSubscribe, refreshUnread]);

    useEffect(() => {
        const onRead = () => refreshUnread();
        window.addEventListener(STORE_CHAT_READ_EVENT, onRead);
        return () => window.removeEventListener(STORE_CHAT_READ_EVENT, onRead);
    }, [refreshUnread]);

    // 시트가 열린 동안에는 ESC 로 닫고 배경 스크롤을 막는다
    useEffect(() => {
        if (!menuOpen) return;
        const onKey = (e) => {
            if (e.key === 'Escape') setMenuOpen(false);
        };
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = prevOverflow;
            document.removeEventListener('keydown', onKey);
        };
    }, [menuOpen]);

    /*
     * 화면이 '실제로 바뀐 경우에만' 시트를 닫는다 (뒤로가기 포함).
     *
     * ※ 의존성만 [location.pathname] 로 두고 무조건 닫으면 안 된다.
     *   React 가 대기중이던 이펙트를 나중에 몰아서 실행할 때 이 이펙트가 다시 돌면서
     *   방금 연 메뉴를 그 자리에서 닫아버린다. (실제로 메뉴가 열리지 않는 버그가 있었다)
     *   이전 경로와 비교해 달라졌을 때만 닫도록 해서 마운트·재실행에 영향받지 않게 한다.
     */
    const prevPathRef = useRef(location.pathname);
    useEffect(() => {
        if (prevPathRef.current === location.pathname) return;
        prevPathRef.current = location.pathname;
        setMenuOpen(false);
    }, [location.pathname]);

    const logout = () => {
        storeLogout();
        navigate(STORE_LOGIN_PATH, {replace: true});
    };

    const MENUS = [
        {key: 'dashboard', label: '대시보드', icon: <TbLayoutDashboard/>, path: STORE_DASHBOARD_PATH},
        {key: 'apidoc', label: 'API 명세서', icon: <TbApi/>, path: '/store/api/docs'},
        {key: 'chat', label: '채팅문의', icon: <TbBrandHipchat/>, path: '/store/api/chat', badge: unreadChat},
        {key: 'mail', label: '메일문의', icon: <TbMail/>, path: '/store/api/mail'},
        {key: 'kakao', label: '카카오톡문의', icon: <TbMessageDots/>, external: 'https://pf.kakao.com/_zTxllC/chat'},
        {key: 'modify', label: '내정보', icon: <TbUserCog/>, path: '/store/api/modify'},
    ];

    const goMenu = (menu) => {
        setMenuOpen(false);
        if (menu.external) {
            window.open(menu.external, '_blank', 'noopener,noreferrer');
            return;
        }
        navigate(menu.path);
    };

    return (
        <StoreShell theme={theme}>

            {/*═══ 헤더 — 여명 위에 떠 있는 유리 띠 ═══*/}
            <header className="tw-sticky tw-top-0 tw-z-50 tw-border-b tw-px-5 tw-py-3"
                    style={{
                        background: 'var(--st-glass)',
                        borderColor: 'var(--st-glass-line)',
                        backdropFilter: 'blur(18px) saturate(140%)',
                        WebkitBackdropFilter: 'blur(18px) saturate(140%)',
                    }}>
                <div className="tw-mx-auto tw-flex tw-max-w-[1320px] tw-items-center tw-justify-between tw-gap-4">
                    <button type="button" onClick={() => navigate(STORE_DASHBOARD_PATH)}
                            className="tw-flex tw-min-w-0 tw-items-center tw-gap-2.5">
                        <img src={btorageBooster} alt="Btorage" className="st-logo st-logo-sm"/>
                        <span className="st-eyebrow tw-hidden sm:tw-inline">OpenAPI</span>
                    </button>

                    <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2.5">
                        {companyName ? (
                            <span className="tw-hidden tw-max-w-[200px] tw-truncate tw-text-[13px] tw-text-[color:var(--st-text-sub)] sm:tw-inline">
                                {companyName}
                            </span>
                        ) : null}
                        <button type="button" onClick={logout}
                                className="st-btn st-btn-glass st-btn-sm">
                            <TbLogout className="tw-text-[15px]"/>
                            <span className="tw-hidden sm:tw-inline">로그아웃</span>
                        </button>

                        {/* 햄버거 — 1024px 미만에서만 보인다 (st-burger 미디어쿼리) */}
                        <button type="button" className="st-burger"
                                aria-label="메뉴 열기" aria-expanded={menuOpen} aria-controls="store-mobile-menu"
                                onClick={() => setMenuOpen(true)}>
                            <TbMenu2/>
                        </button>
                    </div>
                </div>
            </header>

            {/*═══ 모바일 메뉴 시트 ═══*/}
            {menuOpen ? (
                <div className="st-sheet" onClick={() => setMenuOpen(false)}
                     role="dialog" aria-modal="true" aria-label="상점사 메뉴">
                    <div className="st-sheet-panel" onClick={(e) => e.stopPropagation()}>
                        <div className="st-glass tw-p-2">
                            <div className="tw-flex tw-items-center tw-justify-between tw-px-3 tw-pb-1 tw-pt-2">
                                <p className="st-eyebrow">Menu</p>
                                <button type="button" className="st-burger" aria-label="메뉴 닫기"
                                        onClick={() => setMenuOpen(false)}>
                                    <TbX/>
                                </button>
                            </div>
                            <nav id="store-mobile-menu" aria-label="상점사 메뉴" className="st-nav">
                                {MENUS.map(menu => (
                                    <button key={menu.key} type="button" onClick={() => goMenu(menu)}
                                            aria-current={active === menu.key ? 'page' : undefined}
                                            className={`st-nav-item${active === menu.key ? ' is-active' : ''}`}>
                                        <span className="st-nav-icon" aria-hidden="true">{menu.icon}</span>
                                        <span className="st-nav-label">{menu.label}</span>
                                        {menu.badge > 0 ? (
                                            <span className="st-nav-badge">{menu.badge > 99 ? '99+' : menu.badge}</span>
                                        ) : null}
                                    </button>
                                ))}
                            </nav>
                        </div>
                    </div>
                </div>
            ) : null}

            {/*═══ 본문 ═══*/}
            <div className="tw-mx-auto tw-flex tw-max-w-[1320px] tw-flex-col tw-items-start tw-gap-5 tw-px-4 tw-pb-20 tw-pt-6 lg:tw-flex-row">

                {/*─── 메뉴 레일 (데스크톱 전용) ───
                    1024px 미만에서는 숨기고 헤더의 햄버거로 펼친다 */}
                <nav aria-label="상점사 메뉴"
                     className="st-glass st-rise st-rise-1 st-nav st-nav-rail tw-flex-shrink-0 lg:tw-sticky lg:tw-top-[84px] lg:tw-w-[252px]">
                    {MENUS.map(menu => {
                        const isActive = active === menu.key;
                        return (
                            <button key={menu.key} type="button" onClick={() => goMenu(menu)}
                                    aria-current={isActive ? 'page' : undefined}
                                    className={`st-nav-item${isActive ? ' is-active' : ''}`}>
                                <span className="st-nav-icon" aria-hidden="true">{menu.icon}</span>
                                <span className="st-nav-label">{menu.label}</span>
                                {menu.badge > 0 ? (
                                    <span className="st-nav-badge">{menu.badge > 99 ? '99+' : menu.badge}</span>
                                ) : null}
                            </button>
                        );
                    })}
                </nav>

                {/*─── 컨텐츠 ───*/}
                <main className="tw-w-full tw-min-w-0 tw-flex-1">
                    {(title || desc) ? (
                        <div className="st-rise st-rise-2 tw-mb-5 tw-px-1">
                            {eyebrow ? <p className="st-eyebrow tw-mb-2">{eyebrow}</p> : null}
                            {/*
                               제목이 길어지면 두 줄로 흐른다.
                               break-keep 이라 '님,' 같은 조사가 앞줄에 붙어 끊기지 않고,
                               왼쪽 기준으로 두 줄이 가지런히 맞는다.
                             */}
                            {title ? (
                                <h1 className={`st-display tw-break-keep tw-leading-snug tw-text-[color:var(--st-text)] ${titleClassName || 'tw-text-[26px]'}`}>
                                    {title}
                                </h1>
                            ) : null}
                            {desc ? (
                                <p className="tw-mt-2 tw-text-[13.5px] tw-leading-relaxed tw-text-[color:var(--st-text-faint)] tw-break-keep">
                                    {desc}
                                </p>
                            ) : null}
                        </div>
                    ) : null}
                    {children}
                </main>
            </div>
        </StoreShell>
    );
}

export default StoreLayout;
